import React from 'react';
import moment from 'moment';
import { useForm, useWatch } from 'react-hook-form';
import { Save, Cancel, Close } from '@mui/icons-material';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, Divider, Button } from '@mui/material';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import {
  DealActivityStatus,
  DealActivityEventType,
  DealActivityEventPriority,
  DealActivityParticipationType,
} from '@trnsact/trnsact-shared-types';
import {
  DateField,
  InputField,
  SelectField,
  CheckboxField,
  TimeSelectField,
  AutocompleteValueField,
} from 'components/form';

import { CalendarMapper } from '../../../mapper';
import { CalendarEventCardProps } from '../../../types';
import { useDealActivityMutations } from '../../../../api';
import { calendarApi, useCalendarFormOptions } from '../../../api';
import { calendarEventUpdateFormSchema, CalendarEventUpdateFormValues } from './calendarEventUpdateFormSchema';

const extractTimeFromIso = (isoString: string | null | undefined) => {
  if (!isoString) return null;
  return moment(isoString).format('HH:mm');
};

export const CalendarEventUpdateForm = ({ event, onSwitchMode, handleClose }: CalendarEventCardProps) => {
  const { handleUpdateDealActivity, isDealActivityUpdating } = useDealActivityMutations();

  const startIso = event.allDay ? null : event.startAt;
  const endIso = event.allDay ? null : event.endAt;

  const { control, setValue, handleSubmit } = useForm<CalendarEventUpdateFormValues>({
    resolver: zodResolver(calendarEventUpdateFormSchema),
    defaultValues: {
      allDay: event.allDay,
      subject: event.subject,
      priority: event.priority,
      eventType: event.eventType,
      accountId: event.accountId,
      endTime: extractTimeFromIso(endIso),
      activityStatus: event.activityStatus,
      description: event.description ?? '',
      startTime: extractTimeFromIso(startIso),
      endDate: event.allDay ? (event.endDate ?? null) : (event.endAt ?? null),
      startDate: event.allDay ? (event.startDate ?? null) : (event.startAt ?? null),
    },
  });

  const contactAccountId =
    event.participants.find(participant => participant.participationType === DealActivityParticipationType.External)
      ?.account?.id ?? null;

  const { accountOptions, accountOptionsLoading } = useCalendarFormOptions(contactAccountId);

  const [allDay, startDate] = useWatch({ control, name: ['allDay', 'startDate'] });

  const onSubmit = async (data: CalendarEventUpdateFormValues) => {
    await handleUpdateDealActivity(CalendarMapper.mapFormDtoToUpdate(event, data), () => ({
      awaitRefetchQueries: true,
      refetchQueries: [calendarApi.events.queries.listWithActivities],
    }));

    handleClose();
  };

  return (
    <>
      <Stack height="100%" direction="column" gap="1rem">
        <InputField
          label="Subject"
          name="subject"
          control={control}
          inputProps={{ fullWidth: true, variant: 'outlined', required: true }}
        />

        <Divider sx={{ borderColor: 'divider' }} />

        <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <AutocompleteValueField
            name="accountId"
            control={control}
            options={accountOptions}
            loading={accountOptionsLoading}
            autoHighlight
            textFieldProps={{ label: 'Account', variant: 'outlined', size: 'small', required: true }}
          />

          <SelectField
            label="Type"
            name="eventType"
            control={control}
            variant="outlined"
            options={convertEnumToSelectOptions(DealActivityEventType)}
          />
        </Box>

        <CheckboxField
          label="All day"
          name="allDay"
          control={control}
          extraAction={checked => {
            if (checked) {
              setValue('startTime', null);
              setValue('endTime', null);
            }
          }}
        />

        <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: allDay ? '1fr 1fr' : '1fr 1fr 1fr 1fr' }}>
          <DateField
            label="Start Date"
            name="startDate"
            control={control}
            slotProps={{ textField: { variant: 'outlined', size: 'small', required: true } }}
            extraAction={value => {
              if (value) {
                setValue('endDate', moment(value).toISOString());
              }
            }}
          />

          {!allDay && (
            <TimeSelectField
              required
              name="startTime"
              control={control}
              label="Start Time"
              variant="outlined"
              size="small"
              extraAction={value => {
                if (!value) return;

                const { time, nextDay } = CalendarMapper.addOneHour(value);

                setValue('endTime', time);

                if (nextDay && startDate) {
                  setValue('endDate', moment(startDate).add(1, 'day').toISOString());
                }
              }}
            />
          )}

          <DateField
            label="End Date"
            name="endDate"
            control={control}
            minDate={startDate ? moment(startDate) : undefined}
            slotProps={{ textField: { variant: 'outlined', size: 'small', required: true } }}
          />

          {!allDay && (
            <TimeSelectField
              required
              name="endTime"
              control={control}
              label="End Time"
              variant="outlined"
              size="small"
            />
          )}
        </Box>

        <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <SelectField
            label="Status"
            control={control}
            variant="outlined"
            name="activityStatus"
            options={convertEnumToSelectOptions(DealActivityStatus)}
          />

          <SelectField
            label="Priority"
            name="priority"
            control={control}
            variant="outlined"
            options={convertEnumToSelectOptions(DealActivityEventPriority)}
          />
        </Box>

        <InputField
          label="Description"
          name="description"
          control={control}
          inputProps={{
            minRows: 3,
            multiline: true,
            fullWidth: true,
            variant: 'outlined',
            placeholder: 'Add description...',
          }}
        />
      </Stack>

      <Stack direction="row" gap="1rem" width="100%">
        <Button
          fullWidth
          variant="contained"
          startIcon={<Save />}
          onClick={handleSubmit(onSubmit)}
          loading={isDealActivityUpdating}
        >
          Save
        </Button>

        <Button fullWidth onClick={onSwitchMode} startIcon={<Cancel />} disabled={isDealActivityUpdating}>
          Cancel
        </Button>

        <Button fullWidth onClick={handleClose} startIcon={<Close />} disabled={isDealActivityUpdating}>
          Close
        </Button>
      </Stack>
    </>
  );
};
